// Design System factory and default systems following functional architecture patterns

import type {
  DesignSystem,
  DesignSystemTokens,
  DesignSystemContexts,
  DesignSystemPermissions,
  ProjectType,
} from "../types/project-legacy-types";

// Helper to generate unique IDs
const generateId = (): string => {
  return Date.now().toString(36) + Math.random().toString(36).substr(2);
};

// Default design system contexts based on project types
const DEFAULT_CONTEXTS: DesignSystemContexts = {
  website: {
    fontSizeBase: 16,
    spacingUnit: 8,
    density: "spacious",
    contrast: "high",
    surfaceDepth: "minimal",
  },
  dashboard: {
    fontSizeBase: 13,
    spacingUnit: 4,
    density: "compact",
    contrast: "medium",
    surfaceDepth: "layered",
  },
  app: {
    fontSizeBase: 14,
    spacingUnit: 6,
    density: "balanced",
    contrast: "medium",
    surfaceDepth: "moderate",
  },
};

// Default design system tokens
const DEFAULT_TOKENS: DesignSystemTokens = {
  contexts: DEFAULT_CONTEXTS,
  fontSizes: {
    sm: "contexts.{context}.fontSizeBase - 2",
    base: "contexts.{context}.fontSizeBase",
    md: "contexts.{context}.fontSizeBase + 2",
    lg: "contexts.{context}.fontSizeBase + 4",
    xl: "contexts.{context}.fontSizeBase + 6",
  },
  spacing: {
    xs: "contexts.{context}.spacingUnit * 1",
    sm: "contexts.{context}.spacingUnit * 2",
    md: "contexts.{context}.spacingUnit * 3",
    lg: "contexts.{context}.spacingUnit * 4",
    xl: "contexts.{context}.spacingUnit * 6",
  },
  radii: {
    none: 0,
    sm: 2,
    md: 4,
    lg: 8,
    full: 9999,
  },
  fontWeights: {
    regular: 400,
    medium: 500,
    bold: 700,
  },
  lineHeights: {
    tight: 1.2,
    normal: 1.5,
    relaxed: 1.75,
  },
  colors: {
    neutrals: {
      hue: 240,
      saturation: 5,
      lightnessSteps: {
        L0: 98,
        L1: 94,
        L2: 90,
        L3: 85,
        Linverse: 12,
      },
    },
    surface: {
      base: "hsl(neutrals.hue, neutrals.saturation%, lightnessSteps.L0%)",
      variant1: "hsl(neutrals.hue, neutrals.saturation%, lightnessSteps.L1%)",
      variant2: "hsl(neutrals.hue, neutrals.saturation%, lightnessSteps.L2%)",
      variant3: "hsl(neutrals.hue, neutrals.saturation%, lightnessSteps.L3%)",
      inverse:
        "hsl(neutrals.hue, neutrals.saturation%, lightnessSteps.Linverse%)",
    },
    onSurface: {
      base: "hsl(neutrals.hue, neutrals.saturation * 2%, lightnessSteps.Linverse%)",
      variant:
        "hsl(neutrals.hue, neutrals.saturation * 2%, lightnessSteps.Linverse + 20%)",
      inverse: "hsl(0, 0%, 100%)",
    },
    primary: "hsl(260, 80%, 50%)",
    onPrimary: "hsl(0, 0%, 100%)",
    secondary: "hsl(180, 60%, 45%)",
    onSecondary: "hsl(0, 0%, 0%)",
    error: "hsl(0, 75%, 50%)",
    onError: "hsl(0, 0%, 100%)",
    aliases: {
      background: "colors.surface.base",
      cardBackground: "colors.surface.variant2",
      navbarBackground: "colors.surface.variant1",
      modalBackground: "colors.surface.variant3",
      footerBackground: "colors.surface.inverse",
      textPrimary: "colors.onSurface.base",
      textSecondary: "colors.onSurface.variant",
      textInverse: "colors.onSurface.inverse",
      buttonPrimaryBackground: "colors.primary",
      buttonPrimaryText: "colors.onPrimary",
    },
  },
  shadows: {
    sm: "0 1px 2px rgba(0,0,0,0.05)",
    md: "0 3px 6px rgba(0,0,0,0.1)",
    lg: "0 10px 15px rgba(0,0,0,0.2)",
  },
  zIndices: {
    base: 0,
    dropdown: 1000,
    modal: 1100,
    toast: 1200,
  },
  durations: {
    fast: "150ms",
    normal: "300ms",
    slow: "500ms",
  },
};

/**
 * Creates a design system with default tokens and context-specific optimizations
 *
 * @param name - Name of the design system
 * @param ownerId - User ID of the owner
 * @param scope - Whether it's local to a project or shared
 * @param customTokens - Custom token overrides
 * @returns Complete design system object
 *
 * @example
 * ```typescript
 * const ds = createDesignSystem("My App DS", "user123", "local", {
 *   colors: {
 *     primary: "hsl(220, 90%, 60%)"
 *   }
 * });
 * ```
 */
export const createDesignSystem = (
  name: string,
  ownerId: string,
  scope: "local" | "shared" = "local",
  customTokens: Partial<DesignSystemTokens> = {}
): DesignSystem => {
  const tokens: DesignSystemTokens = {
    ...DEFAULT_TOKENS,
    ...customTokens,
    // Deep merge contexts if provided
    contexts: {
      ...DEFAULT_TOKENS.contexts,
      ...customTokens.contexts,
    },
    // Deep merge colors if provided
    colors: {
      ...DEFAULT_TOKENS.colors,
      ...customTokens.colors,
      aliases: {
        ...DEFAULT_TOKENS.colors.aliases,
        ...customTokens.colors?.aliases,
      },
    },
  };

  const permissions: DesignSystemPermissions = {
    ownerId,
    editors: [],
    viewers: [],
    isPublic: scope === "shared",
  };

  return {
    id: generateId(),
    name,
    version: "1.0.0",
    scope,
    tokens,
    permissions,
    created: new Date(),
    modified: new Date(),
  };
};

/**
 * Creates design system optimized for specific project types with predefined contexts
 */
export const createDesignSystemForProjectType = (
  projectType: ProjectType,
  name: string,
  ownerId: string
): DesignSystem => {
  const contextOptimizations: Record<
    ProjectType,
    Partial<DesignSystemTokens>
  > = {
    website: {
      contexts: {
        ...DEFAULT_CONTEXTS,
        website: {
          ...DEFAULT_CONTEXTS.website,
          density: "spacious",
          surfaceDepth: "minimal",
        },
      },
    },
    dashboard: {
      contexts: {
        ...DEFAULT_CONTEXTS,
        dashboard: {
          ...DEFAULT_CONTEXTS.dashboard,
          density: "compact",
          contrast: "high", // Dashboards need higher contrast
        },
      },
      colors: {
        ...DEFAULT_TOKENS.colors,
        neutrals: {
          ...DEFAULT_TOKENS.colors.neutrals,
          saturation: 3, // Less saturated for professional look
        },
      },
    },
    app: {
      contexts: {
        ...DEFAULT_CONTEXTS,
        app: {
          ...DEFAULT_CONTEXTS.app,
          spacingUnit: 7, // Balanced defaults for general app
          density: "balanced",
        },
      },
    },
  };

  return createDesignSystem(
    name,
    ownerId,
    "local",
    contextOptimizations[projectType]
  );
};

/**
 * Creates a Linear-inspired dark design system
 * Example of how LLM might configure a design system based on user intent
 */
export const createLinearInspiredDesignSystem = (
  name: string,
  ownerId: string
): DesignSystem => {
  const linearTokens: Partial<DesignSystemTokens> = {
    contexts: {
      ...DEFAULT_CONTEXTS,
      website: {
        fontSizeBase: 15, // Slightly smaller, more precise
        spacingUnit: 6, // Tighter spacing
        density: "compact", // Linear-like density
        contrast: "high", // Dark theme needs high contrast
        surfaceDepth: "flat", // Fine lines, minimal depth
      },
    },
    colors: {
      ...DEFAULT_TOKENS.colors,
      neutrals: {
        hue: 220, // Cool, technical blue
        saturation: 8, // Low saturation, professional
        lightnessSteps: {
          L0: 8, // Dark background
          L1: 12,
          L2: 16,
          L3: 20,
          Linverse: 95, // Light text
        },
      },
      primary: "hsl(260, 100%, 80%)", // Bright purple for dark theme
      secondary: "hsl(120, 100%, 80%)", // Bright green accent
      aliases: {
        ...DEFAULT_TOKENS.colors.aliases,
        background: "colors.surface.base",
        textPrimary: "colors.onSurface.inverse", // Light text on dark
      },
    },
    radii: {
      none: 0,
      sm: 3, // Slightly more rounded than default
      md: 6,
      lg: 12,
      full: 9999,
    },
  };

  return createDesignSystem(name, ownerId, "local", linearTokens);
};

/**
 * Resolves design system token references to actual CSS values
 * This would be used when generating CSS from component data structures
 *
 * @param tokenPath - Dot notation path like "colors.aliases.buttonPrimaryBackground"
 * @param designSystem - The design system to resolve from
 * @param context - The project context (website, dashboard, app)
 * @returns Resolved CSS value
 */
export const resolveDesignSystemToken = (
  tokenPath: string,
  designSystem: DesignSystem,
  context: keyof DesignSystemContexts
): string => {
  // This is a simplified version - real implementation would handle
  // complex reference resolution, mathematical calculations, etc.

  const tokens = designSystem.tokens;
  const pathParts = tokenPath.split(".");

  // Handle context interpolation
  if (tokenPath.includes("{context}")) {
    // Replace {context} with actual context and resolve
    // This would need more sophisticated parsing in real implementation
    return tokenPath.replace("{context}", context);
  }

  // Navigate the token object
  let value: any = tokens;
  for (const part of pathParts) {
    if (value && typeof value === "object" && part in value) {
      value = value[part];
    } else {
      console.warn(`Token path ${tokenPath} not found in design system`);
      return tokenPath; // Return original path as fallback
    }
  }

  return typeof value === "string" || typeof value === "number"
    ? String(value)
    : tokenPath;
};
